import React from "react";
import cns from "classnames";
import { Observer } from "mobx-react";
import { TableCell, withStyles } from "@material-ui/core";
import {
  AutoSizer,
  Column,
  defaultTableRowRenderer,
  IndexRange,
  InfiniteLoader,
  OverscanIndexRange,
  Table,
  TableProps,
  TableCellProps as TableCellPropsOrig,
  TableHeaderProps,
  TableRowProps,
  TableRowRenderer,
  WindowScroller,
  CellMeasurer,
  CellMeasurerCache
} from "react-virtualized";
import { Classes, ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { ColumnWidth, TableSearchColumn } from "./utils";
import { Spinner } from "@components";
import { Label } from "@components/typography/Label";
import ColumnSizer from "./ColumnSizer";
import { VirtualSearchManager } from "../VirtualSearchManager";

type TableCellProps<ROW extends { _id: string }> = TableCellPropsOrig & {
  columnInfo: TableSearchColumn<ROW>;
};

type Props<ROW extends { _id: string }> = {
  classes: Classes<typeof styles>;

  columns: Array<TableSearchColumn<ROW>>;
  disableHeader?: boolean;
  dynamicColIndex?: number;
  scrollContainer: HTMLElement;
  manager: VirtualSearchManager<ROW>;

  headerHeight?: TableProps["headerHeight"];

  rowClassName?: TableProps["rowClassName"];
  rowStyle?: TableProps["rowStyle"];
  rowHeight: TableProps["rowHeight"];
  rowRenderer?: TableRowRenderer;

  onRowClick?: TableProps["onRowClick"];
  onRowDoubleClick?: TableProps["onRowDoubleClick"];
  onRowMouseOut?: TableProps["onRowMouseOut"];
  onRowMouseIn?: TableProps["onRowMouseIn"];
  onRowMouseOver?: TableProps["onRowMouseOver"];

  onRowsRendered?: (params: IndexRange & OverscanIndexRange) => void;
};

class TableSearch<ROW extends { _id: string }> extends React.Component<Props<ROW>> {
  columnSizerRef: ColumnSizer;
  windowScrollerRef: WindowScroller;
  batchSize = localStorage.getItem("table_batch_size") || 50;
  threshold = localStorage.getItem("table_threshold") || 25;

  rowCache: CellMeasurerCache;

  constructor(props: Props<ROW>) {
    super(props);
    this.props.manager.registerUpdateCallback(() => this.forceUpdate());
    this.props.manager.setScrollContainer(this.props.scrollContainer);
    this.rowCache = new CellMeasurerCache({
      defaultHeight: 50,
      minHeight: 50,
      keyMapper: (index) => this.props.manager.keyMapper(index)
    });
  }

  getRowClassName = ({ index }) => {
    const { classes, rowClassName } = this.props;

    // if header
    if (index === -1) {
      return classes.flexContainer;
    }

    const _rowClassName =
      typeof rowClassName === "function" ? rowClassName({ index }) : rowClassName;

    return cns(classes.flexContainer, classes.bodyRow, _rowClassName, {
      [classes.rowOdd]: index % 2 !== 1
    });
  };

  render() {
    const { classes, columns, manager } = this.props;

    return (
      <WindowScroller
        ref={(ref) => (this.windowScrollerRef = ref)}
        scrollElement={this.props.scrollContainer}
      >
        {(wsProps) => {
          if (typeof wsProps.height !== "number") {
            return null;
          }

          return (
            <>
              <InfiniteLoader
                ref={manager.setInfiniteLoaderRef}
                isRowLoaded={manager.isRowLoaded}
                loadMoreRows={manager.loadMoreRows}
                rowCount={10000}
                minimumBatchSize={parseInt(this.batchSize.toString())}
                threshold={parseInt(this.threshold.toString())}
              >
                {({ onRowsRendered, registerChild }) => (
                  <AutoSizer disableHeight>
                    {({ width }) => (
                      <ColumnSizer
                        columns={columns}
                        width={width}
                        ref={(ref) => (this.columnSizerRef = ref)}
                      >
                        {(p) => (
                          <Table
                            style={{ cursor: "pointer" }}
                            autoHeight
                            className={classes.table}
                            headerHeight={this.props.headerHeight}
                            // deferredMeasurementCache={this.rowCache}
                            disableHeader={this.props.disableHeader}
                            // headerClassName={this.getHeaderClassName()}
                            // headerClassName={classes.headerRow}
                            height={wsProps.height}
                            isScrolling={wsProps.isScrolling}
                            noRowsRenderer={this.renderEmpty}
                            rowRenderer={this.rowRenderer}
                            onRowsRendered={(params) => {
                              this.props.onRowsRendered &&
                                this.props.onRowsRendered(params);
                              onRowsRendered(params);
                            }}
                            onScroll={wsProps.onChildScroll}
                            overscanRowCount={10}
                            ref={(ref) => {
                              if (!ref) {
                                return;
                              }

                              manager.setListHolder(ref);
                              registerChild(ref);
                            }}
                            rowClassName={this.getRowClassName}
                            rowCount={manager.rowCount}
                            rowGetter={(index) => manager.getRow(index.index)}
                            rowHeight={this.rowCache.rowHeight}
                            rowStyle={this.props.rowStyle}
                            scrollTop={wsProps.scrollTop}
                            gridStyle={{ outline: "none" }}
                            width={width}
                            onRowClick={this.props.onRowClick}
                            onRowDoubleClick={this.props.onRowDoubleClick}
                            onRowMouseIn={this.props.onRowMouseIn}
                            onRowMouseOut={this.props.onRowMouseOut}
                            onRowMouseOver={this.props.onRowMouseOver}
                          >
                            {columns.filter((x) => !x.skip).map(this.renderColumn)}
                          </Table>
                        )}
                      </ColumnSizer>
                    )}
                  </AutoSizer>
                )}
              </InfiniteLoader>

              <Observer>
                {() => (
                  <div
                    className={classes.emptyRow}
                    style={{
                      display: this.props.manager.isLoading ? "flex" : "none",
                      height: 50
                    }}
                  >
                    <Spinner />
                  </div>
                )}
              </Observer>
            </>
          );
        }}
      </WindowScroller>
    );
  }

  private renderEmpty = () => (
    <Observer>
      {() => (
        <div>
          {!this.props.manager.isLoading && (
            <div className={this.props.classes.emptyRow}>
              <Label>{locale.NoSearchResults}</Label>
            </div>
          )}
        </div>
      )}
    </Observer>
  );

  private renderColumn = (info: TableSearchColumn<ROW>) => {
    const { classes } = this.props;
    const { style } = info;

    const width = this.columnSizerRef.getColumnWidth(info.dataKey);

    const className = cns("table-search-cell", classes.flexContainer, {
      [classes.cellVAlignCenter]: info.vAlign === "center",
      [classes.padded]: !info.fitted
    });

    return (
      <Column
        cellRenderer={(props) => {
          const cellProps = {
            ...props,
            rowData: info.cellRenderer(props),
            columnInfo: info
          };
          if (props.columnIndex === this.props.dynamicColIndex) {
            return this.cellRendererDynamicCache(cellProps);
          } else {
            return this.cellRenderer(cellProps);
          }
        }}
        className={className}
        dataKey={info.dataKey}
        flexGrow={0}
        flexShrink={0}
        headerRenderer={(props) => this.defaultHeaderRenderer({ ...props, width })}
        headerClassName={classes.headerCell}
        key={info.dataKey}
        label={info.label}
        numeric={info.numeric}
        width={width}
        style={style}
      />
    );
  };

  private defaultHeaderRenderer = (
    headerProps: TableHeaderProps & { width: ColumnWidth }
  ) => {
    const { label } = headerProps;
    const { classes, columns } = this.props;

    const column = columns.find((c) => c.dataKey === headerProps.dataKey);

    if (!column) {
      throw new Error("something when wrong, can't fine a column");
    }

    return (
      <TableCell
        component="div"
        className={cns(classes.headerCellInner, classes.flexContainer, classes.noClick)}
        variant="head"
      >
        {label}
      </TableCell>
    );
  };

  private rowRenderer = (rowProps: TableRowProps) => {
    const renderer = this.props.rowRenderer
      ? this.props.rowRenderer
      : defaultTableRowRenderer;
    return renderer(rowProps);
  };

  private cellRenderer = (cellProps: TableCellProps<ROW>) => {
    const { classes } = this.props;

    return (
      <div
        className={classes.flexContainer}
        style={{ alignItems: "center", ...cellProps.columnInfo.innerStyle }}
      >
        {cellProps.rowData}
      </div>
    );
  };

  private cellRendererDynamicCache = (cellProps: TableCellProps<ROW>) => {
    const { classes } = this.props;
    return (
      <CellMeasurer
        cache={this.rowCache}
        columnIndex={0}
        key={cellProps.dataKey}
        parent={cellProps.parent}
        rowIndex={cellProps.rowIndex}
      >
        <div
          className={classes.flexContainer}
          style={{
            alignItems: "center",
            ...cellProps.columnInfo.innerStyle
          }}
        >
          {cellProps.rowData}
        </div>
      </CellMeasurer>
    );
  };
}

const styles = (theme: ThemeType) => ({
  table: {
    fontFamily: theme.typography.fontFamily
  },

  flexContainer: {
    display: "flex",
    boxSizing: "border-box"
  },

  cellVAlignCenter: {
    alignItems: "center"
  },

  bodyRow: {
    // borderBottom: `1px solid ${theme.palette.grey["200"]}`,
    outline: "none",
    transition: theme.transitions.create("background-color"),

    "&:hover": {
      backgroundColor: theme.palette.grey[200]
    }
  },

  rowOdd: {
    backgroundColor: "#e3e3e340"
  },

  flexAuto: {
    flexBasis: ["auto", "!important"]
  },

  headerCell: {
    display: "flex",
    overflowX: "hidden",
    borderBottom: `1px solid ${theme.palette.grey[200]}`
  },

  headerCellInner: {
    flex: 1,
    padding: "0 8px !important",
    border: 0
  },

  tableCell: {
    // flex: 1,
    // padding: "0 8px !important"
  },

  padded: {
    padding: "0 8px"
  },

  noClick: {
    cursor: "initial"
  },

  emptyRow: {
    background: "transparent",
    borderRadius: 0,
    minHeight: 96,
    display: "flex",
    alignItems: "center",
    padding: "16px",
    justifyContent: "center"
  }
});

export default withStyles(styles as any)(TableSearch);
